import { types as sdkTypes } from './sdkLoader';
import { OWN, TUTULIST_LOWERCASE } from './constants';
import { getPayoutFlowState } from './payoutFlow';
import { normalizePhoneNumberToE164 } from './phoneNumber';

const { Money } = sdkTypes;

export const isStripeConnected = user => {
  if (!user?.id) return false;
  return getPayoutFlowState(user).isStripeConnected;
};

export const isPaypalConnected = user => {
  if (!user?.id) return false;
  return getPayoutFlowState(user).isPaypalConnected;
};

export const getPayPalMerchantId = user => {
  if (!user?.id) return null;
  return getPayoutFlowState(user).merchantId;
};

export const getOwnShippingPreference = user => {
  if (!user?.id) return null;
  return user?.attributes?.profile?.protectedData?.shippingPreference || null;
};

export const getOtherShippingPreferences = user => {
  if (!user?.id) return {};
  return user?.attributes?.profile?.protectedData?.otherShippingPreferences || {};
};

export const getThresholdParams = user => {
  if (!user?.id) {
    return {
      thresholdAmount: null,
      thresholdCurrency: null,
      threshold: null,
    };
  }

  const protectedData = user?.attributes?.profile?.protectedData || {};
  const thresholdAmount =
    protectedData?.thresholdAmount && protectedData?.thresholdCurrency
      ? new Money(protectedData.thresholdAmount, protectedData.thresholdCurrency)
      : null;

  return {
    thresholdAmount,
    thresholdCurrency: protectedData?.thresholdCurrency || null,
    threshold: protectedData?.threshold || null,
  };
};

const getFirstDefinedValue = (...values) =>
  values.find(value => value !== undefined && value !== null && value !== '');

const normalizeCountry = country => {
  if (!country) {
    return null;
  }

  if (typeof country === 'string') {
    return country;
  }

  return country.code || country.value || null;
};

const normalizeShippoAddress = address => {
  if (!address) return null;

  const nestedAddress =
    address?.address && typeof address.address === 'object' ? address.address : null;
  const source = nestedAddress || address;

  const line1 = getFirstDefinedValue(source?.line1, source?.street1, source?.addressLine1);
  const line2 = getFirstDefinedValue(source?.line2, source?.street2, source?.addressLine2);
  const line3 = getFirstDefinedValue(source?.line3, source?.street3, source?.addressLine3);
  const zip = getFirstDefinedValue(source?.zip, source?.postalCode, source?.postal_code);
  const phoneNumber = getFirstDefinedValue(
    source?.phoneNumber,
    source?.phone,
    address?.phoneNumber,
    address?.phone
  );
  const country = normalizeCountry(getFirstDefinedValue(source?.country, address?.country));
  const objectId = getFirstDefinedValue(
    source?.objectId,
    source?.object_id,
    address?.objectId,
    address?.object_id
  );

  return {
    ...address,
    ...source,
    objectId: objectId || null,
    object_id: objectId || null,
    line1: line1 || null,
    line2: line2 || null,
    line3: line3 || null,
    zip: zip || null,
    country,
    phoneNumber: normalizePhoneNumberToE164(phoneNumber) || null,
  };
};

export const getShippingDetails = user => {
  const protectedData = user?.attributes?.profile?.protectedData || {};
  const address =
    protectedData?.shippoAddress ||
    protectedData?.shippingAddress ||
    protectedData?.shippingDetails?.address ||
    protectedData?.shippingDetails ||
    null;

  const normalizedAddress = normalizeShippoAddress(address);
  if (!normalizedAddress) {
    return null;
  }

  const phoneNumber = getFirstDefinedValue(
    normalizedAddress?.phoneNumber,
    protectedData?.phoneNumber
  );

  return {
    ...normalizedAddress,
    phoneNumber: normalizePhoneNumberToE164(phoneNumber) || null,
  };
};

export const checkIfEnabledNonUSOrders = user => {
  return user?.id ? user?.attributes?.profile?.protectedData?.enableNonUSOrders ?? 'no' : 'no';
};

export function retrieveUserWishlist(user) {
  if (!user?.id) return null;

  return user.attributes?.profile?.publicData?.wishlist || [];
}
const hasRequiredAddressFields = shippingDetails => {
  if (!shippingDetails) {
    return false;
  }

  const country = String(shippingDetails.country || '').toUpperCase();
  const requiredAddressFields = [
    'line1',
    'city',
    'zip',
    'country',
    ...(['US', 'CA'].includes(country) ? ['state'] : []),
  ];
  return requiredAddressFields.every(field => Boolean(shippingDetails[field]));
};

const hasRequiredDimensions = otherShippingPreferences => {
  const requiredDimensionKeys = ['length', 'width', 'height'];

  return requiredDimensionKeys.every(key => {
    const rawValue = otherShippingPreferences?.[key];
    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return false;
    }

    const parsedValue = Number(rawValue);
    return Number.isFinite(parsedValue) && parsedValue > 0;
  });
};

export const hasValidShippingDetails = user => {
  if (!user?.id) {
    return false;
  }

  const shippingPreference = getOwnShippingPreference(user);

  if (!shippingPreference) {
    return false;
  }

  if (shippingPreference === OWN) {
    return true;
  }

  if (shippingPreference !== TUTULIST_LOWERCASE) {
    return false;
  }

  const shippingDetails = getShippingDetails(user);
  const otherShippingPreferences = getOtherShippingPreferences(user);

  return (
    hasRequiredAddressFields(shippingDetails) && hasRequiredDimensions(otherShippingPreferences)
  );
};
